import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { WorkflowStatus } from '@enview/interface/types/triage/WorkflowStatus';
import { BillTriageAPI } from '../../../api';
import { State } from '../../../dux/@types';
import { useToast } from '../Toast/ToastProvider';
import {
  UNDO_FAILED_MESSAGE,
  UNDO_SUCCEEDED_MESSAGE,
} from './workflowStatusToastMessages';

export type PreviousWorkflowStatus = {
  billId: string;
  // Null when the bill had no status in the workspace; undo then clears it.
  status: WorkflowStatus | null;
};

type UndoWorkflowStatusParams = {
  orgId: number;
  teamId: number;
  previousStatuses: PreviousWorkflowStatus[];
  // Set when the status change also added the bill to workspaces.
  previousWorkspaces?: { billId: string; teamIds: number[] };
};

// Undo usually runs after the component that made the change has unmounted (e.g. a row
// that left the New Bills section), so requests are dispatched directly instead of
// through that component's mutation hooks.
const useUndoWorkflowStatus = (): ((params: UndoWorkflowStatusParams) => void) => {
  const dispatch = useDispatch<ThunkDispatch<State, unknown, AnyAction>>();
  const { showToast } = useToast();

  const restore = async ({
    orgId,
    teamId,
    previousStatuses,
    previousWorkspaces,
  }: UndoWorkflowStatusParams): Promise<void> => {
    // The bulk endpoint applies one status per call, so single and bulk changes share
    // this path with bills grouped by the status they had before.
    const billIdsByStatus = new Map<WorkflowStatus | null, string[]>();
    previousStatuses.forEach(({ billId, status }) => {
      billIdsByStatus.set(status, [...(billIdsByStatus.get(status) ?? []), billId]);
    });
    await Promise.all(
      [...billIdsByStatus].map(([status, billIds]) =>
        dispatch(
          BillTriageAPI.endpoints.bulkUpdateWorkflowStatus.initiate(
            { orgId, teamId, billIds, status },
            { track: false },
          ),
        ).unwrap(),
      ),
    );

    // Runs after the status restore, since the bulk endpoint re-adds each bill to teamId.
    if (previousWorkspaces) {
      await dispatch(
        BillTriageAPI.endpoints.setBillWorkspaces.initiate(
          {
            orgId,
            billId: previousWorkspaces.billId,
            teamIds: previousWorkspaces.teamIds,
          },
          { track: false },
        ),
      ).unwrap();
    }
  };

  return (params: UndoWorkflowStatusParams): void => {
    restore(params).then(
      () => showToast({ message: UNDO_SUCCEEDED_MESSAGE }),
      () => showToast({ message: UNDO_FAILED_MESSAGE, variant: 'error' }),
    );
  };
};

export default useUndoWorkflowStatus;
